import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { api, formatErr } from "../../lib/api";

const VERDICT = {
  safe: { label: "IA: segura", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  suspicious: { label: "IA: suspeita", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: ShieldAlert },
  malicious: { label: "IA: maliciosa", cls: "bg-red-50 text-red-700 border-red-300", Icon: ShieldX },
};

const FLAG_LABELS = {
  pede_pagamento: "pede pagamento", dados_sensiveis: "pede dados sensíveis", cripto_investimento: "cripto/investimento",
  esquema_piramide: "esquema pirâmide/MLM", dinheiro_facil: "dinheiro fácil", contacto_suspeito: "contacto só por chat",
  link_encurtado: "link encurtado", conteudo_adulto: "conteúdo adulto", discriminacao: "discriminação",
  ilegal: "trabalho ilegal", reenvio_encomendas: "reenvio de encomendas", urgencia_excessiva: "urgência excessiva",
  titulo_gritado: "título em maiúsculas", salario_irrealista: "salário irrealista",
};

/** Badge com o resultado da moderação automática (regras + IA) de uma vaga. */
export function AiModerationBadge({ job, onChanged, noAction = false, className = "" }) {
  const [busy, setBusy] = useState(false);
  const m = job?.moderation;
  if (!m) return null;

  const rerun = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/ai-moderate`);
      const v = data.moderation?.verdict;
      toast.success(v ? `Análise IA: ${VERDICT[v]?.label.replace("IA: ", "") || v} → estado ${data.status}` : "Análise IA concluída");
      onChanged && onChanged();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const queued = m.state === "queued";
  const error = m.state === "error";
  const meta = VERDICT[m.verdict];
  // Vagas limpas pelas regras (importadas) — não poluir a UI
  if (m.verdict === "safe" && m.source === "rules" && !(m.flags || []).length) return null;

  const reasons = (m.reasons || []).join(" ");
  const flags = (m.flags || []).map((f) => FLAG_LABELS[f] || f);
  const title = [reasons, flags.length ? `Sinais: ${flags.join(", ")}` : ""].filter(Boolean).join(" — ");

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid={`ai-moderation-${job.id}`}>
      {queued || error || !meta ? (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${error ? "bg-red-50 text-red-700 border-red-200" : "bg-violet-50 text-violet-700 border-violet-200"}`} title={title}>
          {error ? <ShieldAlert className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
          {error ? "IA falhou — rever" : "Em análise pela IA"}
        </span>
      ) : (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`} title={title}>
          <meta.Icon className="w-3 h-3" /> {meta.label}
          {typeof m.risk_score === "number" && m.verdict !== "safe" && <span className="opacity-70">· risco {m.risk_score}</span>}
        </span>
      )}
      {m.human_decision && (
        <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold">
          revisto: {m.human_decision === "approve" ? "aprovado" : "rejeitado"}
        </span>
      )}
      {!noAction && (
        <button type="button" data-testid={`ai-rerun-${job.id}`} onClick={rerun} disabled={busy}
          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
          title="Voltar a analisar com IA">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} reanalisar
        </button>
      )}
      {(m.verdict === "suspicious" || m.verdict === "malicious") && m.reasons?.length > 0 && (
        <p className="w-full text-[11px] leading-snug text-slate-600 line-clamp-2">{m.reasons[0]}</p>
      )}
    </div>
  );
}

/** Faixa de resumo da moderação automática no topo da lista de vagas. */
export function AdminModerationBanner({ onChanged }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/moderation/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const processNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/moderation/process-queue", null, { params: { limit: 100 } });
      toast.success("A analisar a fila com IA — atualiza dentro de 1-2 minutos.");
      setTimeout(() => { load(); onChanged && onChanged(); }, 20000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!stats) return null;
  return (
    <div data-testid="ai-moderation-banner" className="mb-4 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-white p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-violet-600 text-white inline-flex items-center justify-center"><Sparkles className="w-4 h-4" /></span>
        <div>
          <p className="text-sm font-bold text-slate-900">Moderação automática por IA</p>
          <p className="text-[11px] text-slate-500">Anúncios de empresas: sempre IA · Importadas: regras rápidas, só as suspeitas vão à IA</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        <span><b className="text-emerald-700" data-testid="ai-stat-safe">{(stats.safe_ai + stats.safe_rules).toLocaleString("pt-PT")}</b> seguras</span>
        <span><b className="text-amber-700" data-testid="ai-stat-suspicious">{stats.suspicious}</b> suspeitas (pendentes)</span>
        <span><b className="text-red-700" data-testid="ai-stat-malicious">{stats.malicious}</b> maliciosas (rejeitadas)</span>
        <span><b className="text-violet-700" data-testid="ai-stat-queued">{stats.queued}</b> em fila</span>
        {stats.errors > 0 && <span><b className="text-red-600">{stats.errors}</b> com erro</span>}
      </div>
      {(stats.queued > 0 || stats.errors > 0) && (
        <button type="button" data-testid="ai-process-queue-btn" onClick={processNow} disabled={busy}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-1.5 text-xs font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Analisar fila agora
        </button>
      )}
    </div>
  );
}
